import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of, combineLatest, firstValueFrom } from 'rxjs';
import { switchMap, tap, map, startWith, take } from 'rxjs/operators';
import { WorkshopWork, WorkStatus, WorkCurrentStatus, Client, Bike, NotifiedStatus, BillingStatus } from '../core/models';
import { FirestoreDataService } from '../core/firestore-data.service';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { AuthService } from '../core/auth.service';

declare var bootstrap: any;

@Component({
  selector: 'app-ready',
  templateUrl: './ready.component.html',
  styleUrls: ['./ready.component.scss'],
  providers: [DatePipe]
})
export class ReadyComponent implements OnInit, AfterViewInit {
  @ViewChild('notificationModal') notificationModal!: ElementRef;
  private bsNotificationModal: any;

  allReadyWorks$: Observable<WorkshopWork[]>;
  filteredWorks$: Observable<WorkshopWork[]>;
  workSearchControl = new FormControl('');

  selectedWorkId$ = new BehaviorSubject<string | null>(null);
  selectedWorkDetails$: Observable<WorkshopWork | null | undefined>;
  selectedClientDetails$: Observable<Client | null | undefined>;
  selectedBikeDetails$: Observable<Bike | null | undefined>;
  selectedWorkStatus$: Observable<WorkStatus | null>;
  
  workForNotification: WorkshopWork | null = null;
  clientForNotification: Client | null = null;
  
  statusChangeForm: FormGroup;
  
  isUpdatingStatus = false;
  isRevertingStatus = false;
  isUpdatingNotifiedStatus = false;
  isUpdatingBillingStatus = false;

  availableStatusesForChange: { value: WorkCurrentStatus, display: string }[] = [
    { value: 'Ready', display: 'Lista' },
    { value: 'In Progress', display: 'En Progreso' },
    { value: 'Completed', display: 'Completado' },
    { value: 'Handed Off', display: 'Entregado' },
    { value: 'Cancelled', display: 'Cancelado/Desestimado' }
  ];

  constructor(
    public firestoreDataService: FirestoreDataService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private router: Router,
    public authService: AuthService
  ) {
    this.statusChangeForm = this.fb.group({
      newStatus: ['', Validators.required],
      statusNotes: ['']
    });

    // --- SUSCRIPCIÓN A LOS OBSERVABLES DEL SERVICIO ---
    this.allReadyWorks$ = this.firestoreDataService.readyWorks$;

    this.filteredWorks$ = combineLatest([
      this.allReadyWorks$,
      this.workSearchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([works, searchTerm]) => {
        if (!searchTerm) { return works; }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return works.filter(work =>
          (work.clientName?.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (work.bikeDescription?.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (work.caseNumber?.toLowerCase().includes(lowerCaseSearchTerm))
        );
      })
    );
    
    this.selectedWorkDetails$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkshopWorkById(id) : of(null))
    );

    this.selectedClientDetails$ = this.selectedWorkDetails$.pipe(
      switchMap(work => work ? this.firestoreDataService.getClientById(work.clientId) : of(null))
    );
    
    this.selectedBikeDetails$ = this.selectedWorkDetails$.pipe(
      switchMap(work => work ? this.firestoreDataService.getBikeById(work.bikeId) : of(null))
    );

    this.selectedWorkStatus$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkStatus(id) : of(null)),
      tap(status => {
        if (status) {
          this.statusChangeForm.patchValue({ newStatus: status.currentStatus, statusNotes: status.notes });
        } else {
          this.statusChangeForm.reset({ newStatus: '', statusNotes: '' });
        }
      })
    );
  }

  ngOnInit(): void {
    // --- LLAMAMOS AL MÉTODO DE CARGA ---
    this.firestoreDataService.loadReadyWorks();
  }

  ngAfterViewInit(): void {
    if (this.notificationModal?.nativeElement) {
      this.bsNotificationModal = new bootstrap.Modal(this.notificationModal.nativeElement);
    }
  }

  selectWork(work: WorkshopWork): void {
    this.selectedWorkId$.next(work.id || null);
  }

  async onSubmitStatusChange(): Promise<void> {
    const workId = this.selectedWorkId$.value;
    if (!workId || this.statusChangeForm.invalid) {
      alert("Por favor seleccione un estado válido.");
      return;
    }
    
    this.isUpdatingStatus = true;
    const { newStatus, statusNotes } = this.statusChangeForm.value;

    const isManager = await firstValueFrom(this.authService.isManager$);
    console.log(isManager + ' is manager')
    if(!isManager && newStatus === 'In Progress'){
      window.alert("Para regresar el trabajo a en progreso, póngase en contacto con el supervisor");
      this.isUpdatingStatus = false;
      return;
    }
    else{
    
    try {
      if (newStatus === 'Completed') {
        const currentWork = await firstValueFrom(this.selectedWorkDetails$.pipe(take(1)));
        if (currentWork?.billingStatus !== 'billed') {
          alert("Para marcar un trabajo como 'Completado', primero debe estar 'Facturado'.");
          this.isUpdatingStatus = false;
          return;
        }
      }
      await this.firestoreDataService.setWorkStatus(workId, newStatus, statusNotes);
      alert('¡Estado actualizado correctamente!');
      this.selectWork({} as WorkshopWork);
    } catch (error: any) {
      alert(`Error al actualizar el estado: ${error.message}`);
    } finally {
      this.isUpdatingStatus = false;
    }
  }
  }

  async openNotificationModal(work: WorkshopWork): Promise<void> {
    this.workForNotification = work;
    if (work.clientId) {
      this.clientForNotification = await this.firestoreDataService.getClientById(work.clientId);
    } else {
      this.clientForNotification = null;
    }
    if (this.bsNotificationModal) {
      this.bsNotificationModal.show();
    }
  }

  async onNotifyByWhatsapp(): Promise<void> {
    if (!this.workForNotification?.id) return;
    this.isUpdatingNotifiedStatus = true;
    try {
      await this.firestoreDataService.updateNotifiedStatus(this.workForNotification.id, 'notified');
      alert("El estado se ha marcado como 'Notificado'.");
      this.selectedWorkId$.next(this.workForNotification.id);
      if (this.bsNotificationModal) { this.bsNotificationModal.hide(); }
    } catch (error) {
      alert("Hubo un error al actualizar el estado de notificación.");
    } finally {
      this.isUpdatingNotifiedStatus = false;
    }
  }

  async onNotifyByEmail(): Promise<void> {
    if (!this.workForNotification?.id) return;
    const wantsToSendEmail = window.confirm(`¿Confirmas que deseas enviar un correo de notificación a este cliente?`);
    if (!wantsToSendEmail) return;

    this.isUpdatingNotifiedStatus = true;
    try {
      await this.firestoreDataService.sendOrderReadyEmail({
        to_email: this.clientForNotification?.email!,
        client_name: this.clientForNotification?.name!,
        order_number: this.workForNotification.caseNumber,
        pickup_instructions: 'Puedes pasar por nuestro taller en horario habitual.',
        workshop_phone: '809-555-1234' // Reemplaza con tu teléfono
      });
      await this.firestoreDataService.updateNotifiedStatus(this.workForNotification.id, 'notified');
      alert("Notificación por correo enviada y estado actualizado exitosamente.");
      this.selectedWorkId$.next(this.workForNotification.id);
      if (this.bsNotificationModal) { this.bsNotificationModal.hide(); }
    } catch (emailError: any) {
      alert(`Error al programar la notificación por correo: ${emailError.message}`);
    } finally {
      this.isUpdatingNotifiedStatus = false;
    }
  }

  async onBillingStatusChange(event: Event, work: WorkshopWork): Promise<void> {
    if (!work?.id) return;
    this.isUpdatingBillingStatus = true;
    const input = event.target as HTMLInputElement;
    const newStatus: BillingStatus = input.checked ? 'billed' : 'pending';
    try {
      await this.firestoreDataService.updateBillingStatus(work.id, newStatus);
      alert(`Estado de facturación actualizado a: ${newStatus === 'billed' ? 'Facturado' : 'Pendiente de Facturar'}`);
      this.selectedWorkId$.next(work.id);
    } catch (error) {
      console.error("Error al actualizar el estado de facturación:", error);
      alert("Hubo un error al actualizar el estado de facturación.");
      input.checked = !input.checked;
    } finally {
      this.isUpdatingBillingStatus = false;
    }
  }

  async revertToInProgress(workId: string): Promise<void> {
    const isManager = await firstValueFrom(this.authService.isManager$);
    console.log(isManager + ' is manager')
    if(!isManager){
      window.alert("Para regresar el trabajo a en progreso, póngase en contacto con el supervisor");
      return;
    }
    else{
    const confirmation = window.confirm("¿Está seguro de que desea revertir este trabajo a 'En Progreso'? Volverá al panel de servicios activos.");
    if (confirmation) {
      this.isRevertingStatus = true;
      try {
        await this.firestoreDataService.reopenWork(workId);
        alert("¡Trabajo revertido a 'En Progreso' exitosamente!");
        this.selectWork({} as WorkshopWork);
      } catch (error: any) {
        alert(`Error al revertir el trabajo: ${error.message}`);
      } finally {
        this.isRevertingStatus = false;
      }
    }
  }
  }

  // --- FUNCIONALIDAD: IMPRIMIR ORDEN ---
  async printWork(work: WorkshopWork, client: Client, bike: Bike) {
    const statusDoc = await firstValueFrom(this.selectedWorkStatus$.pipe(take(1)));
    
    const printWindow = window.open('', '_blank', 'height=800,width=800');
    if (!printWindow) {
      alert("Su navegador bloqueó la ventana. Por favor permita las ventanas emergentes.");
      return;
    }

    printWindow.document.write('<html><head><title>Detalles de Orden</title>');
    printWindow.document.write(`<style>
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 20px; color: #333; max-width: 800px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .section-title { font-size: 16px; font-weight: bold; background: #eee; padding: 8px; margin-top: 25px; margin-bottom: 10px; border-radius: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 14px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f8f9fa; }
        .text-right { text-align: right; }
        .status-list { list-style-type: none; padding-left: 0; }
        .status-list li { padding: 8px 0; border-bottom: 1px dashed #eee; font-size: 14px; }
    </style>`);
    printWindow.document.write('</head><body>');
    
    // Cabecera
    printWindow.document.write(`<div class="header">
      <h2>Reporte de Orden: ${work.caseNumber}</h2>
      <p style="margin:0; color:#666;">Emitido el: ${new Date().toLocaleString('es-DO')}</p>
    </div>`);
    
    // Info del Cliente y Bici
    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Cliente:</strong> ${client.name}<br><strong>Teléfono:</strong> ${client.phone || 'N/A'}</div>`);
    printWindow.document.write(`<div><strong>Fecha de Recepción:</strong> ${this.datePipe.transform(work.entryDate?.toDate ? work.entryDate.toDate() : work.entryDate, 'dd/MM/yyyy HH:mm', 'es')}<br><strong>Estado Actual:</strong> ${this.getSpanishStatus(work.currentStatus)}</div>`);
    printWindow.document.write('</div>');

    printWindow.document.write('<div class="row">');
    printWindow.document.write(`<div><strong>Bicicleta:</strong> ${bike.brand} ${bike.model || ''} - ${bike.color}<br><strong>Chasis:</strong> ${bike.chassis || 'N/A'}</div>`);
    printWindow.document.write(`<div><h3 style="margin:0;">Total: RD$ ${work.totalPrice}</h3></div>`);
    printWindow.document.write('</div>');

    // Artículos traídos por el cliente
    if (work.clientItems && work.clientItems.length > 0) {
      printWindow.document.write('<div class="section-title">Artículos / Piezas Traídas por el Cliente (Devueltos)</div>');
      printWindow.document.write('<ul style="margin: 5px 0; padding-left: 20px;">');
      work.clientItems.forEach(ci => {
        printWindow.document.write(`<li><strong>${ci}</strong></li>`);
      });
      printWindow.document.write('</ul>');
    }

    // Artículos
    printWindow.document.write('<div class="section-title">Artículos y Servicios Añadidos</div>');
    if (work.addedItems && work.addedItems.length > 0) {
        printWindow.document.write('<table><thead><tr><th>Descripción</th><th>Asignación</th><th>Cant.</th><th class="text-right">Precio Unit.</th><th class="text-right">Subtotal</th></tr></thead><tbody>');
        work.addedItems.forEach(item => {
            const subtotal = item.quantity * item.unitPrice;
            const assignment = (item.mechanicName || item.assistantName) 
              ? `${item.mechanicName ? 'Mec:'+item.mechanicName : ''} ${item.assistantName ? 'Asis:'+item.assistantName : ''}` 
              : 'N/A';
            printWindow.document.write(`<tr>
              <td>${item.productName}</td>
              <td style="font-size:12px; color:#555;">${assignment}</td>
              <td>${item.quantity}</td>
              <td class="text-right">RD$ ${item.unitPrice}</td>
              <td class="text-right">RD$ ${subtotal}</td>
            </tr>`);
        });
        printWindow.document.write('</tbody></table>');
    } else {
        printWindow.document.write('<p>No hay artículos registrados en esta orden.</p>');
    }

    // Historial
    printWindow.document.write('<div class="section-title">Historial de Tiempos y Estados</div>');
    if (statusDoc && statusDoc.history && statusDoc.history.length > 0) {
        printWindow.document.write('<ul class="status-list">');
        statusDoc.history.forEach(h => {
            const dStr = this.datePipe.transform(h.timestamp?.toDate ? h.timestamp.toDate() : h.timestamp, 'dd/MM/yyyy HH:mm', 'es');
            printWindow.document.write(`<li><strong>${dStr}</strong> - ${this.getSpanishStatus(h.status)} ${h.notes ? `<i>(Notas: ${h.notes})</i>` : ''}</li>`);
        });
        printWindow.document.write('</ul>');
    } else {
        printWindow.document.write('<p>No hay registro de historial detallado.</p>');
    }

    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 400);
  }

  getSpanishStatus(statusValue: WorkCurrentStatus | undefined): string {
    if (!statusValue) return 'Desconocido';
    const statusMap: Record<WorkCurrentStatus, string> = {
      'Received': 'Recibido', 'In Progress': 'En Progreso', 'Awaiting Parts': 'Esperando Repuestos',
      'Ready': 'Lista', 'Completed': 'Completado', 'Cancelled': 'Cancelado/Desestimado', 'Handed Off': 'Entregada'
    };
    return statusMap[statusValue] || statusValue;
  }
}